import React, { useState, useMemo } from "react";
import { Search, XCircle, FileSpreadsheet, AlertCircle, Database } from "lucide-react";

const DimensionErrorAnalysis = ({ dimensionDetails }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState(null);

  // Process dimension details to ensure failed_columns exist for both single and multi-file
  const processedDimensionDetails = useMemo(() => {
    if (!dimensionDetails || dimensionDetails.length === 0) return [];

    return dimensionDetails.map(dim => {
      // Case 1: If failed_columns already exists and has data (single file case)
      if (dim.failed_columns && Array.isArray(dim.failed_columns) && dim.failed_columns.length > 0) {
        return dim;
      }

      // Case 2: If there are dimensions_with_issues (multi-file API response)
      if (dim.dimensions_with_issues && Array.isArray(dim.dimensions_with_issues)) {
        const allFailedColumns = [];
        
        dim.dimensions_with_issues.forEach(dimIssue => {
          if (dimIssue.failed_columns && Array.isArray(dimIssue.failed_columns)) {
            allFailedColumns.push(...dimIssue.failed_columns);
          }
        });

        return {
          ...dim,
          failed_columns: allFailedColumns
        };
      }

      // Case 3: Create failed_columns from columns array if it exists (fallback)
      if (dim.columns && Array.isArray(dim.columns) && dim.columns.length > 0) {
        const failed_columns = dim.columns.map(col => ({
          column: col,
          failed: dim.failed || 0,
          total: dim.total || 0,
          passed: dim.passed || 0,
          success_rate: dim.success_rate || 0,
          rule: "Data Quality Validation",
          status: dim.status || "Unknown"
        }));

        return {
          ...dim,
          failed_columns
        };
      }

      // If none exist, return empty failed_columns
      return {
        ...dim,
        failed_columns: []
      };
    });
  }, [dimensionDetails]);

  if (!processedDimensionDetails || processedDimensionDetails.length === 0) return null;

  const handleDimensionClick = (dim) => {
    setSelectedDimension(dim);
    setShowModal(true);
  };

  return (
    <>
      <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
        <div className="flex items-center gap-2 mb-6">
          <Search className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Dimension Error Analysis</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {processedDimensionDetails.map((dim, idx) => {
            const errorSeverity =
              dim.success_rate >= 90 ? "low" : dim.success_rate >= 70 ? "medium" : "high";

            const borderColor =
              errorSeverity === "high"
                ? "border-red-300"
                : errorSeverity === "medium"
                ? "border-yellow-300"
                : "border-green-300";

            const bgColor =
              errorSeverity === "high"
                ? "bg-red-50"
                : errorSeverity === "medium"
                ? "bg-yellow-50"
                : "bg-green-50";

            return (
              <div
                key={idx}
                className={`${bgColor} border-2 ${borderColor} rounded-xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
                onClick={() => handleDimensionClick(dim)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-lg font-bold text-gray-900">{dim.dimension}</h4>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-black ${
                      errorSeverity === "high"
                        ? "bg-red-600 text-white"
                        : errorSeverity === "medium"
                        ? "bg-yellow-600 text-white"
                        : "bg-green-600 text-white"
                    }`}
                  >
                    {dim.failed} Errors
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">Success Rate</span>
                    <span className="font-bold text-gray-900">{dim.success_rate}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">Failed Columns</span>
                    <span className="font-bold text-red-600">{dim.failed_columns?.length || 0}</span>
                  </div>
                </div>

                <button className="w-full py-2 bg-white border-2 border-gray-300 rounded-lg text-sm font-bold text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-all flex items-center justify-center gap-2">
                  Click to view details →
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dimension Detail Modal */}
      {showModal && selectedDimension && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-black mb-1">
                    {selectedDimension.dimension}
                  </h2>
                  <p className="text-blue-100 font-medium">
                    {selectedDimension.failed} errors found •{" "}
                    {selectedDimension.success_rate}% success rate
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
              {selectedDimension.failed_columns && selectedDimension.failed_columns.length > 0 ? (
                selectedDimension.failed_columns.map((column, idx) => (
                  <div
                    key={idx}
                    className="mb-6 pb-6 border-b-2 border-gray-100 last:border-0"
                  >
                    {/* Column Header */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-4">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                          {column.column}
                        </h3>
                        <div className="flex gap-2">
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-black">
                            {column.failed} Failed
                          </span>
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-black">
                            {column.passed || (column.total - column.failed)} Passed
                          </span>
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-black">
                            {column.success_rate}% Success
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">Rule:</span> {column.rule}
                      </div>
                    </div>

                    {/* AI Insight */}
                    {column.ai_insight && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-blue-600" />
                          AI Insights
                        </h4>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {column.ai_insight}
                        </p>
                      </div>
                    )}

                    {/* Sample Failed Records */}
                    {column.failed_sample_details &&
                      column.failed_sample_details.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Database className="w-4 h-4 text-orange-600" />
                            Sample Failed Records
                          </h4>
                          <div className="overflow-x-auto rounded-xl border-2 border-gray-200">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-3 text-left font-black text-gray-700 uppercase">
                                    Row
                                  </th>
                                  <th className="px-4 py-3 text-left font-black text-gray-700 uppercase">
                                    Value
                                  </th>
                                  <th className="px-4 py-3 text-left font-black text-gray-700 uppercase">
                                    Reason
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {column.failed_sample_details.map((record, ridx) => (
                                  <tr key={ridx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-bold text-gray-900">
                                      {record.row}
                                    </td>
                                    <td className="px-4 py-3">
                                      <code className="bg-gray-100 px-2 py-1 rounded text-red-600 font-mono">
                                        {record.value}
                                      </code>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700">
                                      {record.reason}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                    {/* Code Fixes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* SQL Fix */}
                      {column.sql_fix && (
                        <div className="bg-gray-900 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-bold text-white flex items-center gap-2">
                              <Database className="w-4 h-4" />
                              SQL Fix
                            </h4>
                            <button
                              onClick={() => navigator.clipboard.writeText(column.sql_fix)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all"
                            >
                              📋 Copy
                            </button>
                          </div>
                          <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                            <code>{column.sql_fix}</code>
                          </pre>
                        </div>
                      )}

                      {/* Python Fix */}
                      {column.python_fix && (
                        <div className="bg-gray-900 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-bold text-white flex items-center gap-2">
                              🐍 Python Fix
                            </h4>
                            <button
                              onClick={() => navigator.clipboard.writeText(column.python_fix)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all"
                            >
                              📋 Copy
                            </button>
                          </div>
                          <pre className="text-xs text-green-400 font-mono overflow-x-auto">
                            <code>{column.python_fix}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No failed column details available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DimensionErrorAnalysis;